import type { User } from "@supabase/supabase-js";
import { userHasActiveClientPortalAccess } from "@/lib/auth/client-portal-access";
import { pickFirstSafeRedirect } from "@/lib/utils/safe-redirect-path";
import { logger } from "@/lib/logger";
import type { AppRouter, SupabaseBrowserClient } from "./types";

interface RouteAfterSignInArgs {
  supabase: SupabaseBrowserClient;
  router: AppRouter;
  user: User;
  userType: "employee" | "client";
  redirectAfterLogin?: string | null;
  setError: (error: string | null) => void;
}

/**
 * Shared post-authentication routing for any successful sign-in (password,
 * passkey, …). For employees: redirect to the safe `redirectAfterLogin` path or
 * `/dashboard`. For clients: ensure an `app_users` record exists, verify the
 * `client_user` role, confirm active portal access (contact- or company-level),
 * then route to the client dashboard.
 *
 * Returns `true` when navigation was issued, `false` when access was denied
 * (an error message has already been surfaced via `setError`).
 */
export async function routeAfterSignIn(args: RouteAfterSignInArgs): Promise<boolean> {
  const { supabase, router, user, userType, redirectAfterLogin, setError } = args;

  if (userType !== "client") {
    const nextPath = pickFirstSafeRedirect([redirectAfterLogin]) ?? "/dashboard";
    router.push(nextPath);
    return true;
  }

  // Client users: confirm app_users record + role + active portal access.
  let { data: appUser, error: appUserError } = await supabase
    .from("app_users")
    .select("*")
    .eq("id", user.id)
    .single();

  if (appUserError || !appUser) {
    // A verified user with no app_users record yet: create it.
    if (user.email_confirmed_at) {
      const { data: newAppUser, error: createError } = await supabase.rpc(
        "create_app_user_for_verified_user",
        { user_id: user.id }
      );

      if (createError || !newAppUser) {
        setError("Failed to create user profile. Please contact support.");
        return false;
      }

      const { data: createdUser, error: fetchError } = await supabase
        .from("app_users")
        .select("*")
        .eq("id", user.id)
        .single();

      if (fetchError || !createdUser) {
        setError("Failed to retrieve user profile. Please contact support.");
        return false;
      }

      appUser = createdUser;
    } else {
      setError("Please verify your email before logging in.");
      return false;
    }
  }

  const userRole = appUser.roles?.name || appUser.role;
  if (userRole !== "client_user") {
    setError("Access denied. Client access required.");
    return false;
  }

  // Active contact_user_access OR company_user_access (company-wide grant).
  const portalAccess = await userHasActiveClientPortalAccess(supabase, user.id);

  if (!portalAccess.ok) {
    if (portalAccess.message === "no_grants") {
      setError("No client portal access found. Please contact your administrator.");
    } else {
      logger.error("Client portal access check failed", portalAccess.message);
      setError("Unable to verify your portal access. Please try again or contact support.");
    }
    return false;
  }

  router.push("/contacts/dashboard");
  return true;
}
